import React, {Component} from 'react';
import { array, func, object, string } from 'prop-types';
import Select from 'react-select';
import { pluck } from 'ramda';
import CheckCircle from 'react-feather/dist/icons/check-circle';

import AppointmentDateContainer from '../../AppointmentDate/AppointmentDateContainer';
import AppointmentBodyContainer from '../../AppointmentBody/AppointmentBodyContainer';
import DetailMobileView from '../../DetailMobileView/DetailMobileViewContainer';
import PortalDialog from './../../../../PortalDialog';

import cx from 'classnames';
import s from './UpcomingAppointmentItemContainer.css';

const accessibleValueRenderer = (option) => (
  <span className="Select-value-label">{option.label}</span>
);

export default class UpcomingAppointmentItemContainer extends Component {
  static propTypes = {
    appt: object,
    authenticatedPreferences: object,
    cancel: func,
    confirm: func,
    closeConfirmDialog: func,
    defaultEmailRecipient: string,
    email: func,
    hideFullPageLoadingState: func,
    openConfirmDialog: func,
    possibleMessageRecipients: array,
    sendAppointmentMessage: func,
    showFullPageLoadingState: func,
  };

  constructor(props) {
    super(props);

    // Initialize all state variables
    this.state = {
      mobileDetailsView: false,
      emailModalOpen: false,
      emailModalRecipient: '',
      appointmentMessage: {
        open: false,
        recipients: [],
        message: '',
      },
    };
  }

  // Setting state from props is (generally) a React anti-pattern. However, the exception here is that the defaultEmailRecipient prop
  // is acting as seed data for this component's internally-controlled state.
  componentWillReceiveProps(nextProps) {
    if (nextProps.defaultEmailRecipient) {
      this.setState({
        emailModalRecipient: nextProps.defaultEmailRecipient,
      });
    }
  }

  callDoctor = () => window.location='tel:' + this.props.appt.location.phone;

  showMobileDetails = () => this.setState({ mobileDetailsView: true });

  calculateAppointmentActions = () => {
      // Build all available actions - the label/action keys are required for react-select options to render
      const messageAction = { label: 'Message', action: this.openAppointmentMessageDialog };
      const cancelAction = { label: 'Cancel', action: this.openCancelDialog };
      const confirmAction = { label: 'Confirm', action: this.openConfirmDialog };
      const mobileCancelAction = {
          label: 'Cancel Appointment',
          action: () => this.openCancelDialog(),
          cancelButtonStyleRules: true
      };
      const mobileConfirmAction = {
          label: 'Confirm Appointment',
          action: () => this.openConfirmDialog(),
          confirmButtonStyleRules: true
      };
      const emailAction = {label: 'Email Appt. Details', action: this.openAppointmentEmailDialog};
      const callAction = {label: 'Call', action: this.callDoctor};

      this.desktopActions = []
      if (this.props.appt.actions.canMessage) {
          this.desktopActions.push(messageAction);
      }
      if (this.props.appt.actions.canEmail) {
          this.desktopActions.push(emailAction);
      }
      
      this.mobileActions = [ messageAction, callAction ];
      
      // Check if confirm/cancel is an available option, then build all mobile & desktop actions
      if (this.props.authenticatedPreferences.appointmentViewingEnabled && this.props.appt.actions.canConfirm) {
          this.desktopActions.push(confirmAction);
          this.mobileActions.push(mobileConfirmAction);
      }
      if (this.props.authenticatedPreferences.appointmentCancellationEnabled && this.props.appt.actions.canCancel) {
          this.desktopActions.push(cancelAction);
          this.mobileActions.push(mobileCancelAction);
      }
  };

  /**
   * Pop open an appointment email dialog enabling a patient the ability
   * to email their appointment details to anyone.
   */
  openAppointmentEmailDialog = () => this.setState(prevState => ({ emailModalOpen: true }));

  /**
   * Pop open an appointment message dialog enabling a patient the ability
   * to message a member of a practice in regards to their selected appointment.
   */
  openAppointmentMessageDialog = () => this.setState(prevState => ({
    appointmentMessage: {
      open: true
    },
  }));

  /**
   * Pop open a cancel appointment dialog with a set of actions for the patient
   */
  openConfirmDialog = () =>
    this.props.openConfirmDialog({
      title: 'Confirm Appointment',
      content: 'Are you sure you want to confirm your appointment?',
      cancelCallback: this.props.closeConfirmDialog,
      okCallback: this.handleConfirmAppointment,
    });

  /**
   * Pop open a cancel appointment dialog with a set of actions for the patient
   */
  openCancelDialog = () =>
    this.props.openConfirmDialog({
      title: 'Cancel Appointment',
      content: 'Are you sure you want to cancel your appointment?',
      cancelCallback: this.props.closeConfirmDialog,
      okCallback: this.handleCancelAppointment,
    });

  /**
   * Creates a confirm appointment request, calls the API, and updates
   * the dialog based on the API response
   */
  handleConfirmAppointment = () => {
    // Build the AppointmentsResource.confirmAppointment() API Request Object
    const appointment = {
      id: this.props.appt.id,
      externalId: this.props.appt.externalId,
    };

    this.props.confirm(appointment);

    // Return true so AuthLayout will close the confirmDialog
    return true;
  };

  /**
   * Creates a cancel appointment request, calls the API, and updates
   * the dialog based on the API response
   */
  handleCancelAppointment = () => {
    // Build the AppointmentsResource.cancelAppointment() API Request Object
    const appointment = {
      id: this.props.appt.id,
      externalId: this.props.appt.externalId,
    };

    this.props.cancel(appointment);

    // Return true so AuthLayout will close the confirmDialog
    return true;
  };

  handleCancelMessageModal = () => this.setState({ appointmentMessage: { open: false } });

  handleOkMessageModal = () => {
    if (!this.state.appointmentMessage.message || !this.state.appointmentMessage.recipients) {
      return;
    }

    const messageData = {
      apptId: this.props.appt.id,
      apptExternalId: this.props.appt.externalId,
      locationId: this.props.appt.location.id,
      locationExternalId: this.props.appt.location.externalId,
      recipients: this.state.appointmentMessage.recipients ? pluck('value')(this.state.appointmentMessage.recipients) : [],
      message: this.state.appointmentMessage.message
    };

    const successCallback = () => this.handleCancelMessageModal();

    this.props.sendAppointmentMessage(messageData, successCallback);
  };

  handleEmailRecipientChange = (value) => this.setState({ emailModalRecipient: value });

  handleEmailModalClose = () =>
    this.setState({
      emailModalOpen: false,
      emailModalRecipient: this.props.defaultEmailRecipient,
    });

  submitEmailAppointment = () => {
    const appointment = {
      appointmentId: this.props.appt.id,
      recipient: this.state.emailModalRecipient,
    };

    // Success and Error callback is the same, just hide fullPageLoadingState
    const callback = () => this.props.hideFullPageLoadingState();

    this.props.showFullPageLoadingState();

    this.props.email(appointment, callback, callback);
  };

  handleMessageRecipientChange = (value) => {
    this.setState({
      appointmentMessage: {
        ...this.state.appointmentMessage,
        recipients: value,
      }
    });
  };

  // Each of the options passed in from the constructor has an action - call it's function based on it's label
  handleActionChange = (option) => {
    if (option.action) option.action();
  };

  updateMessage = (event) => {
    this.setState({
      appointmentMessage: {
        ...this.state.appointmentMessage,
        message: event.target.value,
      }
    });
  };

  render = () => {
    // Need to calculate the available actions each time before render page, so that the actions dropdown is up to date in real time
    this.calculateAppointmentActions();
    const status = this.props.appt.status;

    return (
      <section onClick={this.showMobileDetails} className={cx({[`${s.mobileDetailView}`]: this.state.mobileDetailsView })}>

        <div className={s.component}>
            {status === "scheduled" &&
                <CheckCircle
                    color="#277B7C"
                    size={50}
                    style={{paddingTop:25}}
                />
            }
          <AppointmentDateContainer date={this.props.appt.date} />
          <AppointmentBodyContainer appt={this.props.appt} />
          <div className={s.actionsContainer}>
            <Select
              required
              searchable={false}
              clearable={false}
              id={`UpcomingAppointment-actions-${this.props.appt.id}`}
              name={`UpcomingAppointment-actions-${this.props.appt.id}`}
              className={cx(s.reactSelectControl, "appointments")}
              optionClassName="appointments__option"
              placeholder="Actions..."
              options={this.desktopActions}
              onChange={this.handleActionChange}
              inputProps={{title:"Upcoming Appointment Actions"}}
              valueRenderer={accessibleValueRenderer}
            />
          </div>
        </div>

        <DetailMobileView appt={this.props.appt} actions={this.mobileActions} />

        <PortalDialog
          id="appointmentMessageModal"
          cancelCallback={this.handleCancelMessageModal}
          okCallback={this.handleOkMessageModal}
          open={this.state.appointmentMessage.open}
          title="Send Message"
          okLabel="Send"
          content={
            <form>
              <div className={s.group}>
                <Select
                  required
                  searchable={true}
                  clearable={false}
                  id="appointmentMessage__recipients"
                  name="appointmentMessage__recipients"
                  placeholder="Select one or more recipients"
                  options={this.props.possibleMessageRecipients}
                  value={this.state.appointmentMessage.recipients}
                  onChange={this.handleMessageRecipientChange}
                  multi
                  inputProps={{title:'Select Recipients'}}
                />
              </div>

              <div className={s.group}>
                <label htmlFor="appointmentMessage__message" className={s.hidden}>Message</label>
                <textarea
                  className={cx(s.control, s.textarea)}
                  placeholder="Your message here..."
                  rows="5"
                  name="appointmentMessage__message"
                  id="appointmentMessage__message"
                  value={this.state.appointmentMessage.message}
                  onChange={this.updateMessage}
                  required
                />
              </div>
            </form>
          }
          modal
        />

        <PortalDialog
          modal
          id="emailAppointmentModal"
          open={this.state.emailModalOpen}
          title="Email Appointment"
          cancelCallback={() => this.handleEmailModalClose()}
          okCallback={() => {
            this.handleEmailModalClose();
            this.submitEmailAppointment();
          }}
          content={
            <div className={s.group}>
              <input
                id="emailAppointmentModal__emailInput"
                className={s.control}
                type="email"
                value={this.state.emailModalRecipient}
                placeholder="Type Email..."
                onChange={event => this.handleEmailRecipientChange(event.target.value)}
                required
              />
            </div>
          }
        />

      </section>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/Pages/Appointments/UpcomingAppointments/UpcomingAppointmentItem/UpcomingAppointmentItemContainer.js